import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '@cbm/shared';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireAuth?: boolean;
}

export function ProtectedRoute({ 
  children, 
  allowedRoles, 
  requireAuth = true 
}: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  // Redirect to login if authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (allowedRoles && user) {
    // Check if user has any of the allowed roles in their roles array
    const userRoles = user.roles || [user.role];
    const hasAccess = allowedRoles.some(allowedRole => 
      userRoles.includes(allowedRole) || user.role === allowedRole
    );
    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

// Higher-order component for role-based route protection
export function withRoleProtection(allowedRoles: UserRole[]) {
  return function RoleProtectedRoute({ children }: { children: React.ReactNode }) {
    return (
      <ProtectedRoute allowedRoles={allowedRoles}>
        {children}
      </ProtectedRoute>
    );
  };
}

// Specific role-based route components
export const PlantOperatorRoute = withRoleProtection(['PLANT']);
export const PlantManagerRoute = withRoleProtection(['PLANT']);
export const OEMServiceRoute = withRoleProtection(['OEM_SERVICE']);
export const OEMEngineeringRoute = withRoleProtection(['OEM_ENGINEERING']);
export const QualityRoute = withRoleProtection(['QUALITY']);

// Multi-role route components
export const PlantRoute = withRoleProtection(['PLANT']);
export const OEMRoute = withRoleProtection(['OEM_SERVICE', 'OEM_ENGINEERING']);
export const AllRolesRoute = withRoleProtection(['PLANT', 'OEM_SERVICE', 'OEM_ENGINEERING', 'QUALITY']);